import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase.js'

// Two hooks for Path A managed posting:
//   useIsOperator()     — boolean. Drives whether the Operator tab
//                         even renders for this user.
//   useOperatorQueue()  — fetches the queue + exposes markAsPosted.
//
// `is_operator()` is a Postgres function that checks the operators
// table for the current auth.uid(). Same RPC backs the RLS policies
// on marketing_drafts, so the UI gate matches the DB gate exactly.
export function useIsOperator() {
  const [isOperator, setIsOperator] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const { data, error } = await supabase.rpc('is_operator')
        if (cancelled) return
        if (error) {
          // eslint-disable-next-line no-console
          console.warn('[useIsOperator] failed', error)
          setIsOperator(false)
        } else {
          setIsOperator(!!data)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  return { isOperator, loading }
}

export function useOperatorQueue() {
  const [queue, setQueue]     = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data, error } = await supabase.rpc('operator_queue')
      if (error) throw error
      setQueue(data || [])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useOperatorQueue] failed', e)
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const markAsPosted = useCallback(async (draftId, notes = null) => {
    const { error } = await supabase.rpc('mark_draft_posted', {
      p_draft_id: draftId,
      p_notes: notes,
    })
    if (error) throw error
    await fetchAll()
  }, [fetchAll])

  return { queue, loading, error, refresh: fetchAll, markAsPosted }
}
